import React, { useState, useEffect, useCallback } from 'react';
import {
    TextField,
    Button,
    IconButton,
    InputAdornment,
    Box,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    InputBase,
    Pagination,
    Paper,
    Divider,
    Stack,
} from '@mui/material';
import {
    Visibility,
    VisibilityOff,
    Lock,
    Search,
    Person,
} from '@mui/icons-material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { API_BASE_URL_SECURITY } from '../../../../utils/config';
import { toast } from 'react-toastify';
import Modal from '../../../../components/Modal/Modal';

const generatePassword = () => uuidv4().slice(0, 12);

const UserForm = ({ isSidebarOpen }) => {
    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState(generatePassword());
    const [nombre, setNombre] = useState('');
    const [apellido, setApellido] = useState('');
    const [email, setEmail] = useState('');
    const [empresaSeleccionada, setEmpresaSeleccionada] = useState('');
    const [empresas, setEmpresas] = useState([]);
    const [rolSeleccionado, setRolSeleccionado] = useState('');
    const [roles, setRoles] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [editingUserId, setEditingUserId] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [dialogSuccess, setDialogSuccess] = useState(false);

    // Búsqueda y paginación
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    const [searchQuery, setSearchQuery] = useState('');

    // Modal de eliminación
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    // ===============================
    // Fetch de catálogos y usuarios
    // ===============================
    const fetchEmpresas = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL_SECURITY}empresas`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem('SecurityToken') || ''}`,
                },
            });
            const data = Array.isArray(response.data) ? response.data : [];
            if (data.length === 0) {
                toast.info('No se encontraron empresas');
            }
            setEmpresas(data);
        } catch (error) {
            console.error(error);
            toast.error('Error al obtener empresas');
        }
    }, []);

    const fetchRoles = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL_SECURITY}roles`);
            setRoles(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error(error);
            toast.error('Error al obtener roles');
        }
    }, []);

    const fetchUsuarios = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL_SECURITY}usuarios`);
            const data = Array.isArray(response.data) ? response.data : [];
            if (data.length === 0) {
                toast.info('No se encontraron usuarios');
            }
            setUsuarios(data);
        } catch (error) {
            console.error(error);
            toast.error('Error al obtener usuarios');
        }
    }, []);

    useEffect(() => {
        fetchEmpresas();
        fetchRoles();
        fetchUsuarios();
    }, [fetchEmpresas, fetchRoles, fetchUsuarios]);

    // ===============================
    // Lógica formulario
    // ===============================
    const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

    const handleGeneratePassword = () => {
        setPassword(generatePassword());
    };

    const clearForm = () => {
        setNombre('');
        setApellido('');
        setEmail('');
        setPassword(generatePassword());
        setEmpresaSeleccionada('');
        setRolSeleccionado('');
        setEditingUserId(null);
    };

    const handleSubmit = async () => {
        if (!nombre || !apellido || !email || !empresaSeleccionada || !rolSeleccionado) {
            toast.error('Todos los campos son obligatorios');
            return;
        }

        const usuarioData = {
            nombre,
            apellido,
            email,
            password,
            empresaId: empresaSeleccionada,
            idRol: rolSeleccionado,
            estatus: 1,
        };

        try {
            if (editingUserId) {
                await axios.put(
                    `${API_BASE_URL_SECURITY}usuarios/${editingUserId}`,
                    usuarioData
                );
                setDialogMessage('Usuario actualizado exitosamente');
            } else {
                await axios.post(`${API_BASE_URL_SECURITY}usuarios`, usuarioData);
                setDialogMessage('Usuario creado exitosamente');
            }

            setDialogSuccess(true);
            setDialogOpen(true);
            await fetchUsuarios();
            clearForm();
        } catch (error) {
            console.error(error);
            setDialogMessage('Error al procesar el usuario.');
            setDialogSuccess(false);
            setDialogOpen(true);
        }
    };

    const handleEditClick = (usuario) => {
        setEditingUserId(usuario._id);
        setNombre(usuario.NOMBRE || '');
        setApellido(usuario.APELLIDO || '');
        setEmail(usuario.EMAIL || '');
        setEmpresaSeleccionada(usuario.EMPRESA_ID || '');
        setRolSeleccionado(usuario.ID_Rol || '');
        setPassword(''); // en blanco = conserva contraseña actual
    };

    // ===============================
    // Eliminación con Modal
    // ===============================
    const handleOpenDeleteModal = () => {
        if (!editingUserId) {
            toast.error('Seleccione un usuario para eliminar');
            return;
        }
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
    };

    const confirmDeleteUser = async () => {
        if (!editingUserId) {
            setIsDeleteModalOpen(false);
            return;
        }

        try {
            await axios.delete(`${API_BASE_URL_SECURITY}usuarios/${editingUserId}`);
            setDialogMessage('Usuario eliminado exitosamente');
            setDialogSuccess(true);
            setDialogOpen(true);

            setUsuarios((prev) => prev.filter((u) => u._id !== editingUserId));
            clearForm();
        } catch (error) {
            console.error(error);
            setDialogMessage('Error al eliminar el usuario.');
            setDialogSuccess(false);
            setDialogOpen(true);
        } finally {
            setIsDeleteModalOpen(false);
        }
    };

    // ===============================
    // Búsqueda y paginación
    // ===============================
    const handleSearchChange = (event) => {
        setSearchQuery(event.target.value);
        setCurrentPage(1);
    };

    const filteredUsuarios = usuarios.filter((usuario) =>
        `${usuario.NOMBRE || ''} ${usuario.APELLIDO || ''} ${usuario.EMAIL || ''}`
            .toLowerCase()
            .includes(searchQuery.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentUsuarios = filteredUsuarios.slice(indexOfFirstItem, indexOfLastItem);

    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 3,
                alignItems: 'flex-start',
                padding: 3,
                marginTop: '25px',
                marginLeft: isSidebarOpen ? '250px' : '160px',
                transition: 'margin-left 0.3s ease-in-out',
                backgroundColor: '#f3f4f6',
                minHeight: 'calc(100vh - 25px)',
            }}
        >
            {/* Panel principal: formulario */}
            <Paper
                elevation={3}
                sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '999px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                        }}
                    >
                        <Person sx={{ color: '#1d4ed8' }} />
                    </Box>
                    <Box>
                        <Typography variant="h5" fontWeight={600}>
                            {editingUserId ? 'Editar usuario' : 'Nuevo usuario'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gestione las cuentas de acceso y sus roles en la plataforma.
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                <TextField
                    fullWidth
                    label="Nombre"
                    margin="normal"
                    variant="outlined"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    required
                />
                <TextField
                    fullWidth
                    label="Apellido"
                    margin="normal"
                    variant="outlined"
                    value={apellido}
                    onChange={(e) => setApellido(e.target.value)}
                    required
                />
                <TextField
                    fullWidth
                    label="Correo electrónico"
                    margin="normal"
                    variant="outlined"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    type="email"
                    required
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Empresa</InputLabel>
                    <Select
                        value={empresaSeleccionada}
                        onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                        label="Empresa"
                        required
                    >
                        {empresas.map((empresa) => (
                            <MenuItem key={empresa._id} value={empresa._id}>
                                {empresa.nombre}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal">
                    <InputLabel>Rol</InputLabel>
                    <Select
                        value={rolSeleccionado}
                        onChange={(e) => setRolSeleccionado(e.target.value)}
                        label="Rol"
                        required
                    >
                        {roles.map((rol) => (
                            <MenuItem key={rol._id} value={rol._id}>
                                {rol.nombre}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    fullWidth
                    label="Contraseña"
                    margin="normal"
                    variant="outlined"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    helperText={
                        editingUserId
                            ? 'Si dejas este campo en blanco se mantendrá la contraseña actual.'
                            : 'Se generó una contraseña temporal que puedes editar.'
                    }
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <Lock />
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton onClick={togglePasswordVisibility} edge="end">
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                <Box sx={{ mt: 1, display: 'flex', gap: 1.5 }}>
                    <Button
                        variant="outlined"
                        onClick={handleGeneratePassword}
                        startIcon={<Lock />}
                    >
                        Generar contraseña
                    </Button>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: '#111827',
                            color: '#fff',
                            py: 1.2,
                            '&:hover': { backgroundColor: '#000' },
                        }}
                        onClick={handleSubmit}
                    >
                        {editingUserId ? 'Guardar cambios' : 'Crear usuario'}
                    </Button>

                    {editingUserId && (
                        <Stack direction="row" spacing={1.5}>
                            <Button
                                variant="outlined"
                                fullWidth
                                onClick={clearForm}
                            >
                                Cancelar edición
                            </Button>
                            <Button
                                variant="outlined"
                                color="error"
                                fullWidth
                                onClick={handleOpenDeleteModal}
                            >
                                Eliminar usuario
                            </Button>
                        </Stack>
                    )}
                </Box>
            </Paper>

            {/* Panel lateral: listado */}
            <Paper
                elevation={2}
                sx={{
                    flex: 1,
                    p: 3,
                    borderRadius: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}
            >
                <Box>
                    <Typography variant="h6" fontWeight={600}>
                        Usuarios registrados
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Seleccione un usuario para editar sus datos y permisos.
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: 1,
                        mb: 1,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 2,
                        border: '1px solid #d1d5db',
                        backgroundColor: '#f9fafb',
                    }}
                >
                    <Search sx={{ mr: 1, fontSize: 20, color: '#6b7280' }} />
                    <InputBase
                        placeholder="Buscar por nombre, apellido o correo…"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        sx={{ flex: 1, fontSize: 14 }}
                    />
                </Box>

                <List
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        maxHeight: 320,
                        overflowY: 'auto',
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#ffffff',
                    }}
                >
                    {currentUsuarios.map((usuario) => (
                        <ListItem key={usuario._id} disablePadding>
                            <ListItemButton onClick={() => handleEditClick(usuario)}>
                                <ListItemText
                                    primary={`${usuario.NOMBRE} ${usuario.APELLIDO}`}
                                    secondary={usuario.EMAIL}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                            </ListItemButton>
                        </ListItem>
                    ))}
                    {currentUsuarios.length === 0 && (
                        <Box sx={{ p: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                No hay usuarios que coincidan con la búsqueda.
                            </Typography>
                        </Box>
                    )}
                </List>

                <Pagination
                    count={Math.ceil(filteredUsuarios.length / itemsPerPage) || 1}
                    page={currentPage}
                    onChange={handlePageChange}
                    size="small"
                    sx={{ alignSelf: 'center', mt: 1 }}
                />
            </Paper>

            {/* Modal de confirmación de eliminación */}
            <Modal isOpen={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '999px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'rgba(248, 113, 113, 0.1)',
                            }}
                        >
                            <WarningAmberRoundedIcon sx={{ color: '#dc2626' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" fontWeight={600}>
                                Eliminar usuario
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Se eliminará la cuenta de{' '}
                                <b>
                                    {nombre || 'este usuario'} {apellido}
                                </b>
                                . Esta acción no se puede deshacer.
                            </Typography>
                        </Box>
                    </Stack>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 1 }}>
                        <Button variant="outlined" onClick={handleCloseDeleteModal}>
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="error"
                            onClick={confirmDeleteUser}
                        >
                            Eliminar definitivamente
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Dialog de feedback (éxito / error) */}
            <Dialog open={dialogOpen} onClose={handleDialogClose}>
                <DialogTitle>{dialogSuccess ? 'Éxito' : 'Error'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">{dialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose} color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default UserForm;
